import { useEffect, useSyncExternalStore } from "react";
import api from "../../utils/api";
import { saveNextTalk } from "../../pwa/nextTalk";

/**
 * The student's Updates list (backend: custom_auth/student_update_views.py) and the unread
 * count behind the dot on the bell. The count is one small request: right away, when the
 * tab comes back or gets focus, and every 2 minutes while it is visible. Nothing is kept
 * between sign-ins: the count lives in memory only.
 */

const BASE = "/api/student-updates";
const quiet = { skipErrorToast: true };
export const POLL_MS = 2 * 60 * 1000;
export const CHANGED_EVENT = "anna:student-updates-changed";

export const fetchUpdates = () => api.get(`${BASE}/`, quiet).then((r) => r.data);
export const fetchUnread = () => api.get(`${BASE}/`, { ...quiet, params: { only: "count" } }).then((r) => r.data);
export const markAllRead = () => api.post(`${BASE}/read/`, { all: true }, quiet).then((r) => r.data);

let state = { unread: 0, ready: false };
const listeners = new Set();
let timer = null;
let users = 0;
let inFlight = null;
let talkChecked = false;

const emit = () => listeners.forEach((listener) => listener());
const set = (patch) => {
  state = { ...state, ...patch };
  emit();
};

export const getUpdatesState = () => state;

/** Something changed for the student (a talk moved, was cancelled…): keep the offline card right. */
const refreshNextTalk = () =>
  api
    .get("/api/booking/appointments/", quiet)
    .then((r) => saveNextTalk(r.data.appointments || []))
    .catch(() => {});

export const refreshUpdatesCount = () => {
  if (inFlight) return inFlight;
  inFlight = fetchUnread()
    .then((data) => {
      const unread = Number(data.unread) || 0;
      const rose = state.ready && unread > state.unread;
      // Once per page load, and again whenever a new update comes in
      if (!talkChecked || rose) {
        talkChecked = true;
        refreshNextTalk();
      }
      set({ unread, ready: true });
    })
    .catch(() => {})
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

/** The sheet calls this after marking rows read. */
export const updatesChanged = (patch) => {
  if (patch) set(patch);
  window.dispatchEvent(new CustomEvent(CHANGED_EVENT));
};

const onVisible = () => {
  if (document.visibilityState === "visible") refreshUpdatesCount();
};

const start = () => {
  refreshUpdatesCount();
  timer = window.setInterval(() => {
    if (document.visibilityState === "visible") refreshUpdatesCount();
  }, POLL_MS);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", onVisible);
};

const stop = () => {
  window.clearInterval(timer);
  timer = null;
  document.removeEventListener("visibilitychange", onVisible);
  window.removeEventListener("focus", onVisible);
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

let owner = null;

/** The unread count for ``who`` (the signed-in student's id); the first user starts the gentle check. */
export const useUpdatesState = (who) => {
  useEffect(() => {
    // Someone else signed in on this browser: start from nothing
    if (who !== owner) {
      owner = who;
      state = { unread: 0, ready: false };
      talkChecked = false;
      emit();
    }
    users += 1;
    if (users === 1) start();
    return () => {
      users -= 1;
      if (users === 0) stop();
    };
  }, [who]);
  return useSyncExternalStore(subscribe, getUpdatesState);
};
